// idbManager.js
import Pako from 'pako';
import { openDB, type IDBPDatabase } from 'idb';
import { GzipDecompressionWorker } from './GzipDecompressionWorker';
import { requireResources } from '../store/assertions';
import { LoadingStatus, setLoadingStatus } from '../store/LoadingStore';
import useNotificationStore from '../Gui/store/notification';
import { NotificationType } from '@visualizer/types';

const DB_VERSION = 4;
const MINOR_VERSION = 4;
const STORE_NAME = `cachedFiles_v${DB_VERSION}.${MINOR_VERSION}`;
const METADATA_STORE_NAME = `metadata_v${DB_VERSION}.${MINOR_VERSION}`;
const LAST_ACCESS_KEY = 'lastAccess'; // Key for accessing last access timestamp
const GRID_NAME_KEY = 'gridName';

const CACHE_LIFETIME = 1 * 7 * 24 * 60 * 60 * 1000; // milliseconds in one week. days - hours - minutes - seconds
const LARGE_WRITE_THRESHOLD_BYTES = 50 * 1024 * 1024; // 50MB
const STORAGE_SAFETY_MARGIN_BYTES = 100 * 1024 * 1024; // 100MB
const FILE_TOO_LARGE_MESSAGE = 'The software crashed because the file is too large. Clear the cached files and try again.';
const GZIP_DECOMPRESSION_FAILED_MESSAGE = 'Lines file is too large to be loaded in the browser';
let persistentStorageAttempted = false;

function handleIndexedDbError(userMessage: string, error: unknown): Error {
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    console.error(`[IndexedDB] ${userMessage}`, normalizedError);
    useNotificationStore.getState().showNotification(userMessage, NotificationType.Error, true, true);
    setLoadingStatus(LoadingStatus.ERROR);
    return normalizedError;
}

function isQuotaExceededError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    if (error.name === 'QuotaExceededError') return true;
    return error.message.toLowerCase().includes('quota');
}

function isGzipArrayBuffer(buffer: ArrayBuffer): boolean {
    if (buffer.byteLength < 2) return false;
    const bytes = new Uint8Array(buffer, 0, 2);
    return bytes[0] === 0x1f && bytes[1] === 0x8b;
}

async function requestPersistentStorageIfAvailable() {
    if (persistentStorageAttempted) return;
    persistentStorageAttempted = true;
    if (typeof navigator === 'undefined' || !navigator.storage?.persist) return;

    try {
        const alreadyPersistent = (await navigator.storage.persisted?.()) ?? false;
        if (alreadyPersistent) return;
        const granted = await navigator.storage.persist();
        console.log(`[IndexedDB] Persistent storage request: ${granted ? 'granted' : 'not granted'}`);
    } catch (error) {
        console.warn('[IndexedDB] Persistent storage request failed', error);
    }
}

async function ensureStorageHeadroom(bytesToWrite: number) {
    if (bytesToWrite < LARGE_WRITE_THRESHOLD_BYTES) return null;
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;

    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage ?? 0;
    const quota = estimate.quota ?? 0;
    if (!quota) return null;

    const freeBytes = Math.max(0, quota - usage);
    console.log(`[IndexedDB] Storage estimate: usage=${(usage / 1e6).toFixed(1)}MB quota=${(quota / 1e6).toFixed(1)}MB`);
    if (freeBytes < bytesToWrite + STORAGE_SAFETY_MARGIN_BYTES) {
        throw new Error(FILE_TOO_LARGE_MESSAGE);
    }
    return {
        usageBytes: usage,
        quotaBytes: quota,
        freeBytes,
        safeFreeBytes: Math.max(0, freeBytes - STORAGE_SAFETY_MARGIN_BYTES),
    };
}

async function writeRecord(db: IDBPDatabase<unknown>, payload: unknown) {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await store.put(payload);
    await tx.done;
}

async function clearCurrentCacheStore(db: IDBPDatabase<unknown>) {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    await store.clear();
    await tx.done;
}

async function cleanupCacheForQuotaRecovery() {
    try {
        const databases = await indexedDB.databases();
        await Promise.all(
            databases.map(async (dbInfo) => {
                if (!dbInfo.name || dbInfo.name === requireResources().gridId) return;
                await new Promise<void>((resolve) => {
                    const request = indexedDB.deleteDatabase(dbInfo.name!);
                    request.onsuccess = () => resolve();
                    request.onerror = () => resolve();
                    request.onblocked = () => resolve();
                });
            })
        );
    } catch (error) {
        console.warn('[IndexedDB] Failed to cleanup old databases during quota recovery', error);
    }
}

async function writeWithQuotaRecovery(db: IDBPDatabase<unknown>, payload: unknown, sizeHintBytes: number) {
    try {
        await ensureStorageHeadroom(sizeHintBytes);
        await writeRecord(db, payload);
    } catch (error) {
        if (!isQuotaExceededError(error)) {
            throw error;
        }

        useNotificationStore
            .getState()
            .showNotification('Local cache is full. Trying to free up space and retry...', NotificationType.Warning, true, true);
        await cleanupCacheForQuotaRecovery();
        await clearCurrentCacheStore(db).catch((cleanupError) => {
            console.warn('[IndexedDB] Failed to clear current cache during quota recovery', cleanupError);
        });
        await ensureStorageHeadroom(sizeHintBytes);
        await writeRecord(db, payload);
    }
}

// Function to delete a database
export async function deleteDatabase() {
    try {
        const dbName = requireResources().gridId;
        const dbExists = await indexedDB.databases().then((dbs) => dbs.some((db) => db.name === dbName));
        if (dbExists) {
            return new Promise<void>((resolve, reject) => {
                const request = indexedDB.deleteDatabase(dbName);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
                request.onblocked = () => reject(new Error('Delete operation blocked'));
            });
        }

        throw new Error('Database does not exist');
    } catch (error) {
        throw handleIndexedDbError('Unable to clear local cache data.', error);
    }
}

export async function deleteAllDatabases() {
    try {
        const databases = await indexedDB.databases();
        const deletionPromises = databases.map((db) => {
            return new Promise<void>((resolve, reject) => {
                const request = indexedDB.deleteDatabase(db.name!);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error);
                request.onblocked = () => reject(new Error(`Delete operation blocked for ${db.name}`));
            });
        });

        return Promise.all(deletionPromises);
    } catch (error) {
        throw handleIndexedDbError('Unable to clear saved offline data.', error);
    }
}

async function openFilesDatabase(): Promise<IDBPDatabase<unknown>> {
    try {
        await requestPersistentStorageIfAvailable();
        return await openDB(requireResources().gridId, DB_VERSION, {
            upgrade(db, oldVersion) {
                // On any schema upgrade, wipe existing stores so cache starts fresh.
                if (oldVersion > 0) {
                    const existingStores = Array.from(db.objectStoreNames);
                    existingStores.forEach((storeName) => db.deleteObjectStore(storeName));
                }

                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME, { keyPath: 'key' });
                }
                if (!db.objectStoreNames.contains(METADATA_STORE_NAME)) {
                    const metaStore = db.createObjectStore(METADATA_STORE_NAME);
                    metaStore.put(Date.now(), LAST_ACCESS_KEY); // Initialize last access timestamp
                    const gridName = requireResources().gridResources.name ?? requireResources().gridId;
                    metaStore.put(gridName, GRID_NAME_KEY);
                }
            },
        });
    } catch (error) {
        throw handleIndexedDbError('Unable to open local storage.', error);
    }
}

async function updateLastAccess(db: IDBPDatabase<unknown>) {
    try {
        const tx = db.transaction(METADATA_STORE_NAME, 'readwrite');
        const store = tx.objectStore(METADATA_STORE_NAME);
        store.put(Date.now(), LAST_ACCESS_KEY);
        const gridName = requireResources().gridResources.name ?? requireResources().gridId;
        store.put(gridName, GRID_NAME_KEY);
        await tx.done;
        await cleanOldDatabases();
    } catch (error) {
        throw handleIndexedDbError('Unable to update local cache metadata.', error);
    }
}

export async function setArrayBuffer(key: string, arrayBuffer: ArrayBuffer) {
    try {
        const db = await openFilesDatabase();
        await updateLastAccess(db); // Update last access before writing new data
        console.log('ArrayBuffer size:', arrayBuffer.byteLength);
        await writeWithQuotaRecovery(db, { key, dataMap: arrayBuffer }, arrayBuffer.byteLength);
    } catch (error) {
        if (isQuotaExceededError(error) || (error instanceof Error && error.message === FILE_TOO_LARGE_MESSAGE)) {
            throw handleIndexedDbError(FILE_TOO_LARGE_MESSAGE, error);
        }
        throw handleIndexedDbError('Unable to cache geometry data locally.', error);
    }
}

async function decompressGzipArrayBuffer(arrayBuffer: ArrayBuffer): Promise<ArrayBuffer> {
    if (!isGzipArrayBuffer(arrayBuffer)) {
        return arrayBuffer;
    }

    try {
        return await GzipDecompressionWorker.getInstance().decompress(arrayBuffer);
    } catch (error) {
        console.warn('[IndexedDB] Worker gzip decompression failed, falling back to main thread.', error);
    }

    const DecompressionStreamCtor = (globalThis as { DecompressionStream?: new (format: string) => TransformStream }).DecompressionStream;
    if (DecompressionStreamCtor) {
        const compressedStream = new Blob([arrayBuffer]).stream();
        const decompressedStream = compressedStream.pipeThrough(new DecompressionStreamCtor('gzip'));
        return await new Response(decompressedStream).arrayBuffer();
    }

    const inflated = Pako.inflate(new Uint8Array(arrayBuffer));
    return inflated.buffer.slice(inflated.byteOffset, inflated.byteOffset + inflated.byteLength);
}

export async function getArrayBuffer(key: string) {
    console.log('Fetching ArrayBuffer for key:', key);
    try {
        const db = await openFilesDatabase();
        await updateLastAccess(db);
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const storedData = await store.get(key);
        await tx.done;
        if (!storedData) {
            throw new Error('No cached data found for the specified key.');
        }
        const dataMap = storedData.dataMap;
        if (dataMap instanceof ArrayBuffer) {
            try {
                return await decompressGzipArrayBuffer(dataMap);
            } catch (error) {
                console.error('[IndexedDB] Failed gzip decompression for cached buffer.', error);
                throw new Error(GZIP_DECOMPRESSION_FAILED_MESSAGE);
            }
        } else {
            throw new Error('Cached data is not an ArrayBuffer.');
        }
    } catch (error) {
        if (error instanceof Error && error.message === GZIP_DECOMPRESSION_FAILED_MESSAGE) {
            throw error;
        }
        if (isQuotaExceededError(error) || (error instanceof Error && error.message === FILE_TOO_LARGE_MESSAGE)) {
            throw handleIndexedDbError(FILE_TOO_LARGE_MESSAGE, error);
        }
        throw handleIndexedDbError('Unable to fetch geometry data locally.', error);
    }
}

export async function checkKey(key: string): Promise<boolean> {
    try {
        const db = await openFilesDatabase();
        await updateLastAccess(db);
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const isThere = await store.getKey(key);
        await tx.done;
        return isThere !== undefined;
    } catch (error) {
        throw handleIndexedDbError('Unable to check cached key.', error);
    }
}

export async function getStorageEstimateInfo() {
    return await ensureStorageHeadroom(LARGE_WRITE_THRESHOLD_BYTES);
}

export async function listCachedGrids() {
    const currentGridId = requireResources().gridId;
    const databases = await indexedDB.databases();
    const namedDatabases = databases.map((db) => db.name).filter((name): name is string => Boolean(name));

    const grids = await Promise.all(
        namedDatabases.map(async (name) => {
            try {
                const db = await openDB(name);
                let lastAccess: number | null = null;
                let gridName: string | null = null;
                if (db.objectStoreNames.contains(METADATA_STORE_NAME)) {
                    const tx = db.transaction(METADATA_STORE_NAME, 'readonly');
                    const store = tx.objectStore(METADATA_STORE_NAME);
                    const rawLastAccess = await store.get(LAST_ACCESS_KEY);
                    const rawGridName = await store.get(GRID_NAME_KEY);
                    await tx.done;
                    lastAccess = typeof rawLastAccess === 'number' ? rawLastAccess : null;
                    gridName = typeof rawGridName === 'string' && rawGridName.trim().length > 0 ? rawGridName : null;
                }
                db.close();
                return {
                    name,
                    gridName,
                    lastAccess,
                    isCurrentGrid: name === currentGridId,
                };
            } catch {
                return {
                    name,
                    gridName: null,
                    lastAccess: null,
                    isCurrentGrid: name === currentGridId,
                };
            }
        })
    );

    return grids
        .filter((grid) => grid.lastAccess !== null || grid.isCurrentGrid)
        .sort((a, b) => {
            if (a.isCurrentGrid !== b.isCurrentGrid) return a.isCurrentGrid ? -1 : 1;
            if (a.lastAccess === null && b.lastAccess === null) return a.name.localeCompare(b.name);
            if (a.lastAccess === null) return 1;
            if (b.lastAccess === null) return -1;
            return b.lastAccess - a.lastAccess;
        });
}

export async function deleteGrid(gridName: string) {
    if (!gridName) return;
    if (gridName === requireResources().gridId) {
        throw new Error('Cannot delete currently active grid cache.');
    }

    const maxAttempts = 6;
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
            await new Promise<void>((resolve, reject) => {
                const request = indexedDB.deleteDatabase(gridName);
                request.onsuccess = () => resolve();
                request.onerror = () => reject(request.error ?? new Error(`Delete operation failed for ${gridName}`));
                request.onblocked = () => reject(new Error(`Delete operation blocked for ${gridName}`));
            });
        } catch (error) {
            lastError = error;
        }

        const dbStillExists = await indexedDB.databases().then((dbs) => dbs.some((db) => db.name === gridName));
        if (!dbStillExists) return;

        await new Promise((resolve) => setTimeout(resolve, 250 * attempt));
    }

    const details = lastError instanceof Error ? ` ${lastError.message}` : '';
    throw new Error(`Unable to delete cached grid ${gridName}.${details}`);
}

export async function cleanOldDatabases() {
    try {
        const databases = await indexedDB.databases();
        await Promise.all(
            databases.map(async (dbInfo) => {
                try {
                    if (!dbInfo.name) return;
                    const db = await openDB(dbInfo.name!, DB_VERSION);
                    try {
                        // Ensure the metadata store exists before attempting to access it
                        const tx = db.transaction(METADATA_STORE_NAME, 'readonly');
                        const store = tx.objectStore(METADATA_STORE_NAME);
                        const lastAccess = await store.get(LAST_ACCESS_KEY);
                        const now = Date.now();
                        if (!lastAccess || now - lastAccess > CACHE_LIFETIME) {
                            console.warn(`Database ${dbInfo.name} is older than 2 weeks. Deleting...`);
                            indexedDB.deleteDatabase(dbInfo.name!); // Delete the database if lastAccess is too old or missing
                        }
                    } finally {
                        db.close();
                    }
                } catch (error) {
                    console.warn('Erasing older db', error);
                    indexedDB.deleteDatabase(dbInfo.name!); // Delete the database if lastAccess is too old or missing
                    throw error;
                }
            })
        );
    } catch (error) {
        throw handleIndexedDbError('Unable to maintain local cache storage.', error);
    }
}
